import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Generations from './Generations';
import PaidUsersGenerations from './PaidUsersGenerations';
import Subscriptions from './Subscriptions';
import Orders from './Orders';
import Users from './Users';
import Credits from './Credits';
import Settings from './Settings';
import API_CONFIG from '../config/api';
import { requestManager } from '../utils/requestManager';
import { cacheEventManager } from '../utils/cacheEventManager';

type TabType = 'generations' | 'paid-users-generations' | 'subscriptions' | 'orders' | 'users' | 'credits' | 'settings';
type TimeRangeType = '1' | '7' | '30' | '90' | '365' | 'all';

interface XDesignDashboardProps {
  activeTab: TabType;
}

const TIME_RANGE_OPTIONS: { value: TimeRangeType; label: string }[] = [
  { value: '1', label: '24H' },
  { value: '7', label: '7D' },
  { value: '30', label: '30D' },
  { value: '90', label: '90D' },
  { value: '365', label: '1Y' },
  { value: 'all', label: 'All' },
];

const XDesignDashboard: React.FC<XDesignDashboardProps> = ({ activeTab }) => {
  const { currentUser, logout } = useAuth();
  const [timeRange, setTimeRange] = useState<TimeRangeType>('1');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingTabs, setLoadingTabs] = useState<Set<TabType>>(new Set());
  const previousTabRef = useRef<TabType | null>(null);

  // Helper function to convert timeRange to API format
  const getApiTimeRange = (timeRange: TimeRangeType): string => {
    switch (timeRange) {
      case '1': return '1d';
      case '7': return '7d';
      case '30': return '30d';
      case '90': return '90d';
      case '365': return '365d';
      case 'all': return 'all';
      default: return '30d';
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
    } catch (error: any) {
      console.error('Logout error:', error);
    }
  };

  const handleForceRefresh = async (): Promise<void> => {
    try {
      setIsLoading(true);
      // Clear all pending requests and cache
      requestManager.cancelAllRequests();
      requestManager.clearCache();

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CACHE.CLEAR}`, {
        method: 'POST'
      });
      const data = await response.json();

      if (data.success) {
        // Emit cache cleared event to notify other components
        cacheEventManager.emitCacheCleared();
      }
    } catch (error: any) {
      console.error('Error clearing cache:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle tab changes and loading states
  useEffect(() => {
    // Clear loading state for previous tab if it changed
    if (previousTabRef.current && previousTabRef.current !== activeTab) {

      const previousTab = previousTabRef.current;
      setLoadingTabs(prev => {
        const newSet = new Set(prev);
        newSet.delete(previousTab);
        return newSet;
      });
    }

    // Set loading state for current tab
    setLoadingTabs(prev => new Set(prev).add(activeTab));
    setIsLoading(true);

    previousTabRef.current = activeTab;

    return () => {
      // Cleanup
    };
  }, [activeTab]);

  // Clear loading state when component unmounts or tab changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingTabs(prev => {
        const newSet = new Set(prev);
        newSet.delete(activeTab);
        return newSet;
      });
      setIsLoading(false);
    }, 1000); // Clear loading after 1 second

    return () => clearTimeout(timer);
  }, [activeTab]);

  const renderActiveComponent = (): JSX.Element => {
    const apiTimeRange = getApiTimeRange(timeRange);
    const isCurrentTabLoading = loadingTabs.has(activeTab);

    // Show loading overlay if current tab is loading
    if (isCurrentTabLoading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="relative mx-auto mb-5 h-16 w-16">
              <div className="absolute inset-0 rounded-full border-4 border-orange-200 dark:border-orange-900/40"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-orange-500 animate-spin"></div>
              <div className="absolute inset-3 rounded-full bg-orange-400 opacity-20"></div>
            </div>
            <p className="text-slate-700 dark:text-slate-300 font-medium">Loading {activeTab.replace(/-/g, ' ')} data...</p>
            <p className="text-sm text-slate-500 dark:text-slate-500 mt-2">
              {requestManager.getPendingRequestsCount()} requests pending
            </p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'generations':
        return <Generations timeRange={apiTimeRange} />;
      case 'paid-users-generations':
        return <PaidUsersGenerations timeRange={apiTimeRange} />;
      case 'subscriptions':
        return <Subscriptions timeRange={apiTimeRange} />;
      case 'orders':
        return <Orders timeRange={apiTimeRange} />;
      case 'users':
        return <Users timeRange={apiTimeRange} />;
      case 'credits':
        return <Credits timeRange={apiTimeRange} />;
      case 'settings':
        return <Settings />;
      default:
        return <Generations timeRange={apiTimeRange} />;
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full relative z-10">
      {/* Top Navigation */}
      <header className="flex-shrink-0 border-b border-slate-200/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 backdrop-blur-xl">
        <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Analytics Dashboard
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Track your XDesign AI metrics and performance</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Segmented Time Range Control */}
            <div className="flex items-center rounded-xl bg-slate-100/80 dark:bg-white/5 border border-slate-200/70 dark:border-white/10 p-1 backdrop-blur">
              {TIME_RANGE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTimeRange(option.value)}
                  disabled={isLoading}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                    ${timeRange === option.value
                      ? 'bg-orange-600 text-white shadow-md shadow-orange-600/30'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-white/10'}`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Force Refresh Button */}
            <button
              onClick={handleForceRefresh}
              disabled={isLoading}
              className="btn-solid px-4 py-2 text-sm font-semibold text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              <svg className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {isLoading ? 'Refreshing...' : 'Force Refresh'}
            </button>
          </div>
        </div>
      </header>

      {/* Content Area */}
      {renderActiveComponent()}
    </div>
  );
};

export default XDesignDashboard;
